import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Typography, Spin, Result, Flex } from 'antd';
import { LoginOutlined, LogoutOutlined, UserAddOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/use_api';
import { useNodeInfo } from '../../hooks/use_node_info';
import { acceptNodeInvite } from '../../hooks/use_node_invites';
import NodeLogo from './NodeLogo';
import PieceBackdrop from '../Skargard/PieceBackdrop';
import Fold from '../Skargard/Fold';
// Shares the Gathering guest join page's layout (.gathering-join,
// .gathering-join-card), like ClaimPage, so the public front doors look alike.
import '../Gathering/Gathering.css';

const { Title, Paragraph, Text } = Typography;

const CLOSED = {
    expired: 'This invite has expired. Ask whoever sent it for a new one.',
    revoked: 'This invite was withdrawn. Ask whoever sent it for a new one.',
    used: 'This invite has already been used.',
};

/**
 * /join-node/:token — where an invite link lands (backend
 * routers/node_invites.py). Public: shows the node and who invited you, with
 * sign-in and sign-up for a visitor; signed in, it accepts the invite at once
 * and opens the node — this one, when the link is on the node's own address
 * (the usual case: the backend builds links that way), else the node's own
 * address. A visitor who opened a link on some other node's address is sent
 * to the right one before signing in, so their account lands in the node the
 * invite is for.
 */
function JoinNodePage() {
    const api = useApi();
    const { token } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading, keycloak, refreshProfile, logout } = useAuth();
    const { nodeSlug } = useNodeInfo();
    const [invite, setInvite] = useState(null);
    const [fetching, setFetching] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [acceptError, setAcceptError] = useState(null);

    useEffect(() => {
        api.get(`/api/node-invites/${encodeURIComponent(token)}`)
            .then(data => { if (data) setInvite(data); else setNotFound(true); })
            .catch(() => setNotFound(true))
            .finally(() => setFetching(false));
    }, [api, token]);

    const closed = invite && invite.status !== 'open' ? (CLOSED[invite.status] || 'This invite no longer works.') : null;

    // Signed in and the invite is open: accept, then go where it leads.
    useEffect(() => {
        if (isLoading || !isAuthenticated || !invite || closed || accepting || acceptError) return;
        setAccepting(true);
        acceptNodeInvite(api, token)
            .then(joined => {
                if (joined.is_current) {
                    // Accepting approved this registration; pick that up before
                    // ProtectedRoute reads the status (ClaimPage does the same).
                    return refreshProfile().then(() => navigate('/', { replace: true }));
                }
                window.location.assign(joined.url);
                return undefined;
            })
            .catch(err => { setAcceptError(err?.message || 'Could not join this node'); setAccepting(false); });
    }, [isLoading, isAuthenticated, invite, closed, accepting, acceptError, api, token, navigate, refreshProfile]);

    const redirectUri = window.location.href;
    // The link belongs on the node's own address; signing in anywhere else
    // would put the new account in the wrong node.
    const elsewhere = !!(invite && nodeSlug && invite.node_slug !== nodeSlug);

    return (
        <PieceBackdrop piece={Fold} followTheme>
            <div className="gathering-join">
                {(fetching || isLoading || accepting) ? (
                    <Spin size="large" />
                ) : notFound ? (
                    <Result
                        status="warning"
                        icon={<NodeLogo size={56} />}
                        title="Invite not found"
                        subTitle="This link may be incomplete, or the invite was removed."
                    />
                ) : closed ? (
                    <Result status="warning" icon={<NodeLogo size={56} />} title={`Invite to ${invite.node_name}`} subTitle={closed} />
                ) : acceptError ? (
                    <Result
                        status="error"
                        icon={<NodeLogo size={56} />}
                        title={`Could not join ${invite.node_name}`}
                        subTitle={acceptError}
                        extra={
                            // Usually the wrong account for an e-mailed invite: let the right one sign in.
                            <Button icon={<LogoutOutlined />} onClick={() => logout(window.location.href)}>
                                Sign out and try another account
                            </Button>
                        }
                    />
                ) : (
                    <Card className="gathering-join-card">
                        <Flex vertical gap={16}>
                            <Flex vertical gap={4} align="center">
                                <NodeLogo size={56} />
                                <Title level={3} style={{ margin: '8px 0 0', textAlign: 'center' }}>{invite.node_name}</Title>
                                <Text strong>
                                    {invite.invited_by_name ? `${invite.invited_by_name} invited you to join` : 'You have been invited to join'}
                                </Text>
                                <Paragraph type="secondary" style={{ margin: 0, textAlign: 'center' }}>
                                    {elsewhere
                                        ? `Continue on ${invite.node_name}'s own address to sign in or create an account there.`
                                        : invite.email
                                            ? <>Sign in or create an account with <Text strong>{invite.email}</Text>.</>
                                            : 'Sign in or create an account to join.'}
                                </Paragraph>
                            </Flex>
                            {elsewhere ? (
                                <Button type="primary" icon={<ArrowRightOutlined />} block href={invite.url}>
                                    Continue on {invite.node_name}
                                </Button>
                            ) : (
                                <Flex vertical gap={10}>
                                    <Button
                                        type="primary"
                                        icon={<UserAddOutlined />}
                                        block
                                        onClick={() => keycloak.register({ redirectUri })}
                                    >
                                        Create an account
                                    </Button>
                                    <Button icon={<LoginOutlined />} block onClick={() => keycloak.login({ redirectUri })}>
                                        Log in
                                    </Button>
                                </Flex>
                            )}
                        </Flex>
                    </Card>
                )}
            </div>
        </PieceBackdrop>
    );
}

export default JoinNodePage;
